import { Clock, BookOpen } from 'lucide-react'
import { Link } from 'react-router-dom'
import { CefrBadge } from '@/components/CefrBadge'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import type { ArticleSummary } from '@/types/content'
import type { CefrLevel } from '@/types/common'

const difficultyCopy: Record<string, string> = {
  COMFORTABLE: copy.recommendation.difficultyComfortable,
  STRETCH: copy.recommendation.difficultyStretch,
  CHALLENGING: copy.recommendation.difficultyChallenging,
}

interface ArticleCardProps {
  article: ArticleSummary
}

export function ArticleCard({ article }: ArticleCardProps) {
  const difficultyDots = article.difficulty ?? 0

  return (
    <Link
      to={`/reading/${article.id}`}
      data-testid="article-card"
      className={cn(
        'block rounded-xl border border-border bg-card p-5 shadow-sm transition-all',
        'hover:shadow-md hover:border-primary/40',
        article.isRead && 'opacity-70'
      )}
    >
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <CefrBadge level={article.cefrLevel as CefrLevel} />
          {article.difficultyLabel ? (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {difficultyCopy[article.difficultyLabel] ?? article.difficultyLabel}
            </span>
          ) : null}
        </div>
        {article.isRead && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
            <BookOpen className="h-3 w-3" />
            Read
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="mb-2 line-clamp-2 text-base font-semibold text-foreground leading-snug">
        {article.title}
      </h3>

      {/* Tags */}
      {article.tags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {article.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground capitalize"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          <span>{article.estimatedTime ?? '?'} min</span>
        </div>
        {/* Difficulty dots */}
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                i < Math.round((difficultyDots / 10) * 5)
                  ? 'bg-primary'
                  : 'bg-muted'
              )}
            />
          ))}
        </div>
      </div>
    </Link>
  )
}

